import { create } from 'zustand';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { settleClaimedAccount } from '../lib/anonymousCheckoutClaim';
import { maybeGrantSignupBonus } from '../lib/signupBonus';
import { maybeGrantProMonthlyCredits } from '../lib/proMonthlyCredits';
import { useCreditStore } from './creditStore';

const AUTH_RETURN_TO_KEY = 'la_auth_return_to';

const getCurrentReturnPath = () => {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname}${window.location.search}${window.location.hash}` || '/';
};

const prepareAuthRedirect = (callbackPath: '/auth/callback' | '/auth/reset-password') => {
  if (typeof window === 'undefined') return callbackPath;

  try {
    localStorage.setItem(AUTH_RETURN_TO_KEY, getCurrentReturnPath());
  } catch {
    // Non-critical: auth still works, only the post-login return path is lost.
  }

  return `${window.location.origin}${callbackPath}`;
};

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithGitHub: () => Promise<void>;
  signInWithDiscord: () => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  isLoading: true,
  error: null,

  initialize: async () => {
    try {
      set({ isLoading: true, error: null });

 // CurrentSessionGet
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) throw error;

      set({
        session,
        user: session?.user ?? null,
        isLoading: false,
      });

 // state
      supabase.auth.onAuthStateChange((event, session) => {
        // A verified sign-in on an account opened via anonymous-checkout
        // auto-login evicts the claim session (no-op for everyone else).
        // INITIAL_SESSION covers the magic-link callback consumed before this
        // listener was registered (then no SIGNED_IN is emitted).
        // TOKEN_REFRESHED included so a failed attempt is retried while the
        // tab stays open (the client only remembers successes).
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') && session?.access_token) settleClaimedAccount(session);
        set({
          session,
          user: session?.user ?? null,
        });

        // Drop the previous user's credit balance the moment the session
        // ends — the AI-assistant editor snapshot reads the store
        // synchronously, so a stale balance would be reported to (and leak
        // to) whoever uses this browser next.
        if (event === 'SIGNED_OUT') {
          useCreditStore.getState().reset();
        } else {
          // Keyed on the user id, not just SIGNED_OUT: switching accounts
          // directly emits SIGNED_IN only, and the balance must not carry over.
          useCreditStore.getState().setActiveUser(session?.user?.id ?? null);
        }

        // GA4 conversion tracking. For OAuth (Google) the email-handler
        // branches above never run — this is the only spot we see the
        // redirect-back. Detect first-time signup by comparing created_at vs
        // last_sign_in_at within a 60s window.
        //
        // Supabase emits SIGNED_IN on every page load / token refresh while a
        // session exists, which used to fire sign_up/login dozens of times per
        // user (GA showed 349 sign_up events from 68 users). Dedupe: sign_up
        // at most once per browser (localStorage), login at most once per
        // browser session (sessionStorage).
        if (event === 'SIGNED_IN' && session?.user) {
          try {
            const u = session.user;
            const created = u.created_at ? new Date(u.created_at).getTime() : 0;
            const lastSignIn = u.last_sign_in_at ? new Date(u.last_sign_in_at).getTime() : 0;
            const isNew = Boolean(created && lastSignIn && Math.abs(lastSignIn - created) < 60_000);
            const method = u.app_metadata?.provider || 'email';
            const signupKey = `la_ga_signup_${u.id}`;
            const loginKey = `la_ga_login_${u.id}`;
            if (isNew && !localStorage.getItem(signupKey)) {
              localStorage.setItem(signupKey, '1');
              window.gtag?.('event', 'sign_up', { method });
            } else if (!isNew && !sessionStorage.getItem(loginKey)) {
              sessionStorage.setItem(loginKey, '1');
              window.gtag?.('event', 'login', { method });
            }
            // Value-first one-time gift: 30 AI HQ credits, no strings.
            // Deliberately OUTSIDE the isNew branch: email-confirm flows
            // routinely exceed the 60s isNew window, which would silently
            // deny the bonus to most email signups. The server enforces the
            // real rules (account age < 7 days, once-ever via unique index),
            // so calling on any SIGNED_IN for a young account is safe.
            // maybeGrantSignupBonus has its own sessionStorage guard.
            const ageMs = created ? Date.now() - created : Number.MAX_SAFE_INTEGER;
            if (ageMs < 7 * 24 * 3600_000 && session.access_token) {
              void maybeGrantSignupBonus(u, session.access_token);
            }
            // Pro's monthly AI-credit allowance. No client-side eligibility
            // check on purpose — the server owns "is this a live non-pass
            // subscription" and "has this 30-day window been claimed", and
            // guessing here would deny credits whenever the subscription
            // store hadn't loaded yet.
            if (session.access_token) {
              void maybeGrantProMonthlyCredits(u, session.access_token);
            }
          } catch {
            // Storage unavailable (private mode etc.) — skip tracking rather
            // than re-introducing duplicate events.
          }

          // Anon→cloud continuity at the conversion moment. Independent of the
          // GA/bonus block above (separate try/catch) so a storage hiccup there
          // can't skip it. If the user was working on a local project, adopt it
          // into the cloud so their in-progress work isn't stranded and the
          // next Save targets a real cloud id — a stale local (non-UUID)
          // currentProjectId would otherwise error in the cloud save path
          // (manual Save or the 30s auto-save). Runs once per browser session.
          //
          // No toast here on purpose: on a successful import the work is saved
          // (not stranded), the Save UI reflects it, and the signup-bonus toast
          // owns the welcome moment — firing a second toast would just clobber
          // it in the single-slot toast store.
          try {
            const adoptKey = `la_adopt_project_${session.user.id}`;
            if (!sessionStorage.getItem(adoptKey)) {
              sessionStorage.setItem(adoptKey, '1');
              void import('./projectStore')
                .then(({ useProjectStore }) => useProjectStore.getState().adoptLocalProjectOnSignin())
                .catch(() => { /* best-effort — never break auth on an import hiccup */ });
            }
          } catch {
            // sessionStorage unavailable — skip; skipping a one-time import is
            // safer than risking a double-import, and the saveProject path
            // still tolerates a fresh cloud project on the user's next Save.
          }
        }
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to initialize',
        isLoading: false,
      });
    }
  },

  signInWithEmail: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      set({
        session: data.session,
        user: data.user,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign in',
        isLoading: false,
      });
      throw error;
    }
  },

  signUpWithEmail: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: prepareAuthRedirect('/auth/callback'),
        },
      });

      if (error) throw error;

      set({
        session: data.session,
        user: data.user,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign up',
        isLoading: false,
      });
      throw error;
    }
  },

  signInWithGoogle: async () => {
    try {
      set({ isLoading: true, error: null });

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: prepareAuthRedirect('/auth/callback'),
        },
      });

      if (error) throw error;

      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign in with Google',
        isLoading: false,
      });
      throw error;
    }
  },

  signInWithGitHub: async () => {
    try {
      set({ isLoading: true, error: null });

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: prepareAuthRedirect('/auth/callback'),
        },
      });

      if (error) throw error;

      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign in with GitHub',
        isLoading: false,
      });
      throw error;
    }
  },

  signInWithDiscord: async () => {
    try {
      set({ isLoading: true, error: null });

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'discord',
        options: {
          redirectTo: prepareAuthRedirect('/auth/callback'),
        },
      });

      if (error) throw error;

      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign in with Discord',
        isLoading: false,
      });
      throw error;
    }
  },

  // Passwordless email login: Supabase emails the user a one-time link;
  // clicking it lands on /auth/callback already authenticated. No external
  // provider setup needed — uses the same SMTP that already powers email
  // signup confirmation.
  signInWithMagicLink: async (email: string) => {
    try {
      set({ isLoading: true, error: null });

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: prepareAuthRedirect('/auth/callback'),
        },
      });

      if (error) throw error;

      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to send magic link',
        isLoading: false,
      });
      throw error;
    }
  },

  signOut: async () => {
    try {
      set({ isLoading: true, error: null });

      const { error } = await supabase.auth.signOut();

      if (error) throw error;

      set({
        session: null,
        user: null,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign out',
        isLoading: false,
      });
      throw error;
    }
  },

  resetPassword: async (email: string) => {
    try {
      set({ isLoading: true, error: null });

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: prepareAuthRedirect('/auth/reset-password'),
      });

      if (error) throw error;

      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to reset password',
        isLoading: false,
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
